import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { RoleService } from '../services/auth/role.service';
import { of } from 'rxjs';
import { switchMap, filter, take, tap } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const roleService = inject(RoleService);
  const router = inject(Router);

  console.log('authGuard (Signal): Executing...');

  return toObservable(authService.authReady).pipe(
    tap((ready) =>
      console.log(
        'authGuard (Signal): authReady signal status (from observable):',
        ready
      )
    ),
    filter((ready) => ready),
    take(1),
    switchMap(() => {
      console.log('authGuard (Signal): authReady is true. Checking roles.');
      if (!authService.isLoggedIn()) {
        console.log(
          'authGuard (Signal): Not logged in according to MSAL (unexpected after authReady). Redirecting to unauthorized.'
        );
        router.navigate(['/unauthorized']);
        return of(false);
      }

      const requiredRoles = ['Truth.Read', 'Truth.Write'];
      if (!roleService.hasAnyRole(requiredRoles)) {
        console.log(
          `authGuard (Signal): User does not have any of the required roles (${requiredRoles.join(
            ', '
          )}). Roles: ${roleService
            .userRoles()
            .join(', ')}. Redirecting to unauthorized.`
        );
        router.navigate(['/unauthorized']);
        return of(false);
      }

      const requiresAdmin = route.data?.['requiresAdmin'] === true;
      if (requiresAdmin && !roleService.hasRole('Truth.Write')) {
        console.log(
          'authGuard (Signal): Admin role (Truth.Write) required but not found. Redirecting to unauthorized.'
        );
        router.navigate(['/unauthorized']);
        return of(false);
      }

      console.log('authGuard (Signal): Access GRANTED.');
      return of(true);
    }),
    tap((granted) =>
      console.log('authGuard (Signal): Final decision:', granted)
    )
  );
};

export const adminGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const roleService = inject(RoleService);
  const router = inject(Router);

  return toObservable(authService.authReady).pipe(
    filter((ready) => ready),
    take(1),
    switchMap(() => {
      console.log(
        'adminGuard (Signal): Roles in admin guard:',
        roleService.userRoles()
      );
      if (!roleService.hasRole('Truth.Write')) {
        router.navigate(['/unauthorized']);
        return of(false);
      }
      return of(true);
    })
  );
};

export const readerGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const roleService = inject(RoleService);
  const router = inject(Router);

  return toObservable(authService.authReady).pipe(
    filter((ready) => ready),
    take(1),
    switchMap(() => {
      if (!roleService.hasAnyRole(['Truth.Read', 'Truth.Write'])) {
        router.navigate(['/unauthorized']);
        return of(false);
      }
      return of(true);
    })
  );
};

export const initRolesGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  console.log(
    'initRolesGuard (Signal): Executing. Waiting for authReady signal.'
  );
  return toObservable(authService.authReady).pipe(
    filter((ready) => ready),
    take(1),
    switchMap(() => {
      console.log('initRolesGuard (Signal): authReady is true. Proceeding.');
      return of(true);
    })
  );
};
